import { useState, useEffect, useCallback } from 'react';
import {
  generatePath,
  useLocation,
  useNavigate,
  useParams,
} from 'react-router-dom';
import {
  ButtonPseudo,
  SidePanel,
  SidePanelLoader,
  SidePanelType,
  IconAnonymized,
  formatDate,
  formatBalance,
  PermissionEnum,
  PermissionDenied,
  LinkSecondaryButton,
  ResponseError,
} from '@ui/src';
import { useAppSelector, useAppDispatch } from '@store';
import { useLazyGetPaymentsOrdersBeneficiaryQuery } from './store/services';
import { clearCashCollectionOrder } from './store/slices/list';
import { StatusTitle } from './types';

import ROUTES from '../../routes';

import cn from './styles.module.css';

const OrderRow = ({
  title,
  value,
}: {
  title: string;
  value: string | React.JSX.Element;
}) => (
  <div className={cn.orderValueRow}>
    <div className={cn.orderValueRowTitle}>{title}</div>
    <div className={cn.orderValueRowContent}>{value}</div>
  </div>
);

const CashCollectionBeneficiaryOrder = () => {
  const [panelsIsOpen, setPanelIsOpen] = useState(false);
  const [internalError, setInternalError] = useState('');

  const { orderId } = useParams();

  const {
    cashCollection: {
      list: { cashCollectionOrder },
    },
    transactionsDictionaries: { activePermissions },
  } = useAppSelector((state) => state);

  const navigate = useNavigate();
  const { search } = useLocation();

  const dispatch = useAppDispatch();

  const [getPaymentsOrdersBeneficiary, { isLoading, isFetching }] =
    useLazyGetPaymentsOrdersBeneficiaryQuery();

  const getPaymentsOrdersBeneficiaryAction = useCallback(async (id: string) => {
    setInternalError('');

    const response = (await getPaymentsOrdersBeneficiary(id)) as {
      error?: ResponseError;
    };

    if (response?.error) {
      if (response?.error?.status === 400) {
        if (Array?.isArray(response?.error?.data?.message)) {
          if (response?.error?.data?.message[0]) {
            setInternalError(response?.error?.data?.message[0].value as string);
          }
        } else if (typeof response?.error?.data?.message === 'string') {
          setInternalError(response?.error?.data?.message);
        } else {
          setInternalError('Internal Server Error. Try Again');
        }
      } else {
        setInternalError('Internal Server Error. Try Again');
      }
    }
  }, []);

  const handleClose = useCallback(async () => {
    dispatch(clearCashCollectionOrder());
    setPanelIsOpen(false);

    setTimeout(() => {
      navigate(`${ROUTES.CASH_COLLECTION_ORDERS}${search || ''}`);
    }, 100);
  }, []);

  useEffect(() => {
    setPanelIsOpen(true);

    if (
      orderId &&
      activePermissions?.includes(
        PermissionEnum.PAYMENTS_ORDERS_BENEFICIARIES_LIST,
      )
    ) {
      getPaymentsOrdersBeneficiaryAction(orderId);
    }
  }, [orderId, activePermissions]);

  let content = <div />;

  if (
    activePermissions?.includes(
      PermissionEnum.PAYMENTS_ORDERS_BENEFICIARIES_LIST,
    )
  ) {
    if ((isLoading || isFetching) && !internalError) {
      content = <SidePanelLoader />;
    } else if (!internalError) {
      content = (
        <>
          <OrderRow
            title="Order ID"
            value={cashCollectionOrder?.orderId as string}
          />
          <OrderRow
            title="Order Date"
            value={
              cashCollectionOrder?.orderDate
                ? formatDate(cashCollectionOrder?.orderDate || '', true)
                : ('-')
            }
          />
          <OrderRow
            title="Payment Order ID"
            value={cashCollectionOrder?.paymentOrderId as string}
          />
          <OrderRow
            title="Payment Order Status"
            value={
              StatusTitle[
                // @ts-ignore
                cashCollectionOrder?.paymentOrder?.status as StatusTitle
              ] as string
            }
          />
          <OrderRow
            title="Payment Order Created"
            value={
              cashCollectionOrder?.paymentOrder?.orderCreatedAt
                ? formatDate(
                    cashCollectionOrder?.paymentOrder?.orderCreatedAt || '',
                    true,
                  )
                : '-'
            }
          />
          <OrderRow
            title="Payment Order Accepted"
            value={
              cashCollectionOrder?.paymentOrder?.orderAcceptedAt
                ? formatDate(
                    cashCollectionOrder?.paymentOrder?.orderAcceptedAt || '',
                    true,
                  )
                : '-'
            }
          />
          <OrderRow
            title="Sender ID"
            value={cashCollectionOrder?.senderId as string}
          />
          <OrderRow
            title="Sender Name"
            value={
              cashCollectionOrder?.senderName === '%Anonymized%' ? (
                <IconAnonymized />
              ) : (
                (cashCollectionOrder?.senderName as string)
              )
            }
          />
          <OrderRow
            title="Sender Address"
            value={cashCollectionOrder?.senderAddress as string}
          />
          <OrderRow
            title="Sender DOB"
            value={
              cashCollectionOrder?.senderDob === '%Anonymized%' ? (
                <IconAnonymized />
              ) : (
                formatDate(cashCollectionOrder?.senderDob as string)
              )
            }
          />
          <OrderRow
            title="Local Country"
            value={cashCollectionOrder?.localCountry.name as string}
          />
          <OrderRow
            title="Local Currency"
            value={cashCollectionOrder?.localCurrency.name as string}
          />
          <OrderRow
            title="Local Amount"
            value={formatBalance(
              Number(cashCollectionOrder?.localAmount),
              cashCollectionOrder?.localCurrency,
            )}
          />
          <OrderRow
            title="Destination Country"
            value={cashCollectionOrder?.destCountry.name as string}
          />
          <OrderRow
            title="Destenation Currency"
            value={cashCollectionOrder?.destCurrency.name as string}
          />
          <OrderRow
            title="Destenation Amount"
            value={formatBalance(
              Number(cashCollectionOrder?.destAmount),
              cashCollectionOrder?.destCurrency,
            )}
          />
          <OrderRow
            title="Beneficiary Company"
            value={
              <>
                <div>
                  {
                    cashCollectionOrder?.paymentOrder?.recepientAccount
                      ?.description
                  }
                </div>
                <div className={cn.accNumber}>
                  {
                    cashCollectionOrder?.paymentOrder?.recepientAccount
                      ?.fullNumber
                  }
                </div>
              </>
            }
          />
          <OrderRow
            title="Beneficiary Name"
            value={
              cashCollectionOrder?.beneficiaryName === '%Anonymized%' ? (
                <IconAnonymized />
              ) : (
                formatDate(cashCollectionOrder?.beneficiaryName as string)
              )
            }
          />
          <OrderRow
            title="Payout Location"
            value={cashCollectionOrder?.beneficiaryCity as string}
          />
          {cashCollectionOrder?.paymentOrderId && (
            <LinkSecondaryButton
              text="Open Cash Collection"
              path={generatePath(ROUTES.CASH_COLLECTION_TRANSACTION, {
                transactionId: cashCollectionOrder?.paymentOrderId,
              })}
              target="_blank"
            />
          )}
        </>
      );
    } else if (internalError) {
      content = <div className={cn.internalError}>{internalError}</div>;
    }
  } else {
    content = <PermissionDenied />;
  }

  return (
    <SidePanel
      title="Order"
      isOpen={panelsIsOpen}
      onClose={handleClose}
      type={SidePanelType.Transactions}
      sidepanelExpandedKey="cash-collection-order-panel"
      footer={<ButtonPseudo text="Close" onClick={handleClose} bold />}
      closeOnClickOutside
    >
      {content}
    </SidePanel>
  );
};

export default CashCollectionBeneficiaryOrder;
